// Uploading branding images (office letterheads, invoice signature) to Storage.
//
// Kept separate from brandingImages.js on purpose: that module is imported by
// src/invoices/renderInvoiceHtml.js, which also runs inside the Vercel PDF
// function and must stay free of the Supabase client and browser-only APIs.

import { supabase } from '../supabaseClient';

export const BRANDING_BUCKET = 'branding';
export const MAX_BRANDING_IMAGE_BYTES = 2 * 1024 * 1024;

const EXT_BY_MIME = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg'
};

async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Upload one branding image and return { url, filename } for storing on
 * app_settings.invoiceBranding. Throws with a user-presentable message.
 *
 * `slug` names the slot ('rotterdam-header', 'mumbai-header', 'signature').
 */
export async function uploadBrandingImage(file, slug) {
  if (!file) throw new Error('No file selected');
  if (file.size > MAX_BRANDING_IMAGE_BYTES) throw new Error('image too large (max 2 MB)');

  const ext = EXT_BY_MIME[file.type];
  if (!ext) throw new Error(`unsupported image type (${file.type || 'unknown'})`);

  const buffer = await file.arrayBuffer();
  // Content hash in the path keeps object URLs immutable, so replacing an image
  // publishes a new URL rather than going stale behind the CDN cache.
  const digest = (await sha256Hex(buffer)).slice(0, 12);
  const path = `invoice/${slug}-${digest}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(BRANDING_BUCKET)
    .upload(path, file, {
      contentType: file.type,
      cacheControl: '31536000',
      upsert: true
    });

  if (uploadErr) throw new Error(uploadErr.message);

  const { data } = supabase.storage.from(BRANDING_BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) throw new Error('could not resolve public URL');

  return { url: data.publicUrl, filename: file.name };
}

export default uploadBrandingImage;
